import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Upload } from "lucide-react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  getProductCosts,
  updateProductCost,
  uploadProductCostsFile,
} from "../../utils/api/productApi";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./ProductCostsPage.module.css";

const sanitizeMoneyInput = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const hasDot = cleaned.includes(".");
  const [wholeRaw, ...rest] = cleaned.split(".");
  const whole = wholeRaw || "";
  const decimals = rest.join("").slice(0, 2);

  if (!hasDot) return whole;
  if (!whole && !decimals) return "0.";
  return `${whole || "0"}.${decimals}`;
};

const moneyEqual = (a, b) => {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return Math.abs(aNum - bNum) < 0.0001;
  }
  return String(a ?? "").trim() === String(b ?? "").trim();
};

const isMissingCostValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const ProductCostsPage = () => {
  const { t, locale } = useLocalization();
  const isArabic = locale === "ar";
  const { user } = useAuthUser();
  const { shopData } = useShop();
  const authToken = user?.token;
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shopData?._id)
    ? shopData._id
    : isMongoId(shopData?.id)
    ? shopData.id
    : null;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [template, setTemplate] = useState(null);
  const [templateDownloaded, setTemplateDownloaded] = useState(false);
  const [error, setError] = useState(null);
  const templateReady = Boolean(template?.downloadUrl);
  const uploadDisabled = uploading || !templateDownloaded || !templateReady;
  const localeTag = isArabic ? "ar" : "en-US";
  const currencyCode = useMemo(() => {
    const candidates = [
      shopData?.currencyCode,
      shopData?.currency_code,
      shopData?.currency,
      shopData?.shopCurrency,
    ]
      .map((v) => String(v || "").trim().toUpperCase())
      .filter(Boolean);
    const firstValid = candidates.find((code) => /^[A-Z]{3}$/.test(code));
    return firstValid || "USD";
  }, [shopData]);

  const formatMoney = useCallback(
    (value) => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "—";
      try {
        return new Intl.NumberFormat(localeTag, {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return `${amount.toFixed(2)} ${currencyCode}`;
      }
    },
    [currencyCode, localeTag]
  );

  const currencySymbol = useMemo(() => {
    try {
      const parts = new Intl.NumberFormat(localeTag, {
        style: "currency",
        currency: currencyCode,
      }).formatToParts(0);
      return parts.find((p) => p.type === "currency")?.value || currencyCode;
    } catch {
      return currencyCode;
    }
  }, [currencyCode, localeTag]);

  const loadProducts = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await getProductCosts({ authToken, shopId });
      const list = Array.isArray(resp?.products) ? resp.products : [];
      setProducts(list);
      setTemplate(resp?.template || null);
      setDrafts(
        list.reduce((acc, item) => {
          acc[item.id] = {
            price: item.current_price ?? "",
            cost: item.product_cost ?? "",
          };
          return acc;
        }, {})
      );
    } catch (err) {
      setError(
        err?.message ||
          t("ProductCostsScreen.loadError") ||
          "Failed to load product costs."
      );
    } finally {
      setLoading(false);
    }
  }, [authToken, shopId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const setDraftValue = useCallback((productId, key, value) => {
    const nextValue = sanitizeMoneyInput(value);
    setDrafts((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [key]: nextValue },
    }));
  }, []);

  const updateRow = (updated) => {
    setProducts((prev) =>
      prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
    );
    setDrafts((prev) => ({
      ...prev,
      [updated.id]: {
        price: updated.current_price ?? prev?.[updated.id]?.price ?? "",
        cost: updated.product_cost ?? prev?.[updated.id]?.cost ?? "",
      },
    }));
  };

  const handleUpdateCost = async (productId) => {
    const draft = drafts[productId];
    setSaving((prev) => ({ ...prev, [productId]: true }));
    setError(null);
    try {
      const updated = await updateProductCost({
        productId,
        productCost: draft?.cost ?? null,
        authToken,
      });
      updateRow(updated);
    } catch (err) {
      setError(
        err?.message ||
          t("ProductCostsScreen.updateCostError") ||
          "Failed to update product cost."
      );
    } finally {
      setSaving((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleUpload = async (file) => {
    if (!authToken || uploading) return;
    if (!templateDownloaded) {
      setError(
        t("ProductCostsScreen.downloadFirst") ||
          "Download the template first, then upload."
      );
      return;
    }
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const resp = await uploadProductCostsFile({ file, authToken, shopId });
      const updated = resp?.updated ?? 0;
      const skipped = resp?.skipped ?? 0;
      setError(
        t("ProductCostsScreen.uploadSuccess", {
          updated,
          skipped,
        }) || `Updated ${updated}, skipped ${skipped}.`
      );
      await loadProducts();
    } catch (err) {
      setError(
        err?.message ||
          t("ProductCostsScreen.uploadFailed") ||
          "Upload failed."
      );
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((item) =>
      [item.title, item.handle, item.id]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(q))
    );
  }, [products, query]);

  const hasMixedMissingProductCosts = useMemo(() => {
    let hasMissing = false;
    let hasFilled = false;

    for (const product of products) {
      if (isMissingCostValue(product?.product_cost)) hasMissing = true;
      else hasFilled = true;
      if (hasMissing && hasFilled) return true;
    }
    return false;
  }, [products]);

  return (
    <section
      className={`${pageStyles.page} ${styles.wrapper} ${isArabic ? styles.rtl : ""}`}
      data-page="product-costs"
    >
      <header className={pageStyles.sectionHeader}>
        <h2>{t("ProductCostsScreen.title") || "Product Costs"}</h2>
        {/* <button className={pageStyles.sectionHeaderButton} onClick={loadProducts}>
          {t("ProductCostsScreen.refresh") || "Refresh"}
        </button> */}
      </header>

      <p className={styles.subtitle}>
        {t("ProductCostsScreen.subtitle") ||
          "Update Shopify prices and store internal costs for margin analysis."}
      </p>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("ProductCostsScreen.searchPlaceholder") || "Search products"}
        />
        {template?.downloadUrl && (
          <a
            className={styles.downloadButton}
            href={template.downloadUrl}
            onClick={() => setTemplateDownloaded(true)}
            target="_blank"
            rel="noreferrer"
          >
            <Download size={16} className={styles.actionIcon} aria-hidden="true" />
            {t("ProductCostsScreen.download") || "Download Template"}
          </a>
        )}
        <label
          className={`${styles.uploadButton} ${
            uploadDisabled ? styles.uploadButtonDisabled : ""
          }`}
          aria-disabled={uploadDisabled}
          title={
            uploadDisabled && !uploading
              ? t("ProductCostsScreen.downloadFirst") ||
                "Download the template first, then upload."
              : undefined
          }
        >
          <Upload size={16} className={styles.actionIcon} aria-hidden="true" />
          {uploading
            ? t("ProductCostsScreen.uploading") || "Uploading..."
            : t("ProductCostsScreen.upload") || "Upload Excel"}
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(event) => handleUpload(event.target.files?.[0])}
            disabled={uploadDisabled}
            hidden
          />
        </label>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {loading && (
        <p className={styles.loading}>
          {t("ProductCostsScreen.loading") || "Loading products..."}
        </p>
      )}

      {!loading && !filtered.length && (
        <p className={styles.empty}>
          {t("ProductCostsScreen.empty") || "No products available."}
        </p>
      )}

      {filtered.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("ProductCostsScreen.productLabel") || "Product Name"}</th>
                <th>
                  {t("ProductCostsScreen.priceLabel") || "Current Price"} (
                  {currencyCode})
                </th>
                <th>
                  {t("ProductCostsScreen.costLabel") || "Product Cost"} (
                  {currencyCode})
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => {
                const draft = drafts[product.id] || {};
                const effectiveCostValue =
                  draft.cost !== undefined ? draft.cost : product.product_cost;
                const showMissingCostHighlight =
                  hasMixedMissingProductCosts && isMissingCostValue(effectiveCostValue);
                return (
                  <tr key={product.id}>
                    <td>
                      <div className={styles.productCell}>
                        <p className={styles.productTitle}>{product.title}</p>
                        {/* <p className={styles.productMeta}>
                          {t("ProductCostsScreen.idLabel") || "ID"}:{" "}
                          {product.product_id || product.id}
                        </p> */}
                      </div>
                    </td>
                    <td>
                      <span className={styles.readOnlyValue}>
                        {formatMoney(product.current_price)}
                      </span>
                    </td>
                    <td>
                      <div
                        className={`${styles.costCell} ${
                          showMissingCostHighlight ? styles.costCellMissing : ""
                        }`}
                      >
                        <div className={styles.costInputWrap}>
                          <span
                            className={`${styles.currencyBadge} ${
                              showMissingCostHighlight ? styles.currencyBadgeMissing : ""
                            }`}
                          >
                            {currencySymbol}
                          </span>
                          <input
                            className={`${styles.moneyInput} ${
                              showMissingCostHighlight ? styles.moneyInputMissing : ""
                            }`}
                            value={draft.cost ?? ""}
                            onChange={(event) =>
                              setDraftValue(product.id, "cost", event.target.value)
                            }
                            onBlur={() => {
                              if (!moneyEqual(draft.cost, product.product_cost)) {
                                handleUpdateCost(product.id);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter") return;
                              event.currentTarget.blur();
                            }}
                            placeholder="0.00"
                          />
                        </div>
                        {saving[product.id] ? (
                          <span className={styles.savingText}>
                            {t("ProductCostsScreen.saving") || "Saving..."}
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ProductCostsPage;
